import type { FC, ChangeEvent } from 'react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import { makeXHRCall } from '@wix/da-shared-react/pkg/simpleXHR/actionCreators';
import { getIsLoading } from '@wix/da-shared-react/pkg/simpleXHR/selectors';
import {
  SimpleXHREndpoint,
  SimpleXHRMethod,
} from '@wix/da-shared-react/pkg/simpleXHR/constants';
import { updateBitpayState } from '../../../../../actions/studio/earnings';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import {
  PureBiData,
  StudioEarningsWithdrawalClickBiEvent,
  StudioWithdrawalPaymentMethod,
} from '@wix/da-bi/pkg/events';
import WithdrawalErrorBlock from '../../_partials/WithdrawalErrorBlock';

import s from './BitPayWithdrawSetupModal.scss';

export interface Props {
  isOpen: boolean;
  requestClose: () => void;
  onSuccess?: () => void;
  bitpayEmail?: string;
}

const BITPAY_SIGNUP_URL = 'https://bitpay.com/';

const BitPayWithdrawSetupModal: FC<Props> = ({
  isOpen,
  requestClose,
  onSuccess,
  bitpayEmail,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const logEvent = useBiLogger();
  const isLoading = useSelector((state: any) =>
    getIsLoading(state, SimpleXHREndpoint.BITPAY_CONNECT)
  );
  const [email, setEmail] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [emailValidationError, setEmailValidationError] = useState<string>('');

  // Clear error when modal opens
  useEffect(() => {
    if (isOpen) {
      setErrorMessage('');
      setEmailValidationError('');
      setEmail(bitpayEmail || '');
    }
  }, [isOpen, bitpayEmail]);

  // Clear error when user starts typing
  const clearError = () => {
    if (errorMessage) {
      setErrorMessage('');
    }
    if (emailValidationError) {
      setEmailValidationError('');
    }
  };

  const handleEmailChange = (ev: ChangeEvent<HTMLInputElement>) => {
    setEmail(ev.target.value);
    clearError();
  };

  const isValidEmail = (emailValue: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  };

  const handleContinue = () => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail || !isValidEmail(trimmedEmail)) {
      setEmailValidationError(t('common.error.invalidEmail'));
      return;
    }

    logEvent(
      PureBiData<StudioEarningsWithdrawalClickBiEvent>({
        evid: 159,
        payment_method: StudioWithdrawalPaymentMethod.BitPay,
        process_step: 'setup',
      })
    );
    dispatch(
      makeXHRCall({
        papiBase: 'studio',
        endpoint: SimpleXHREndpoint.BITPAY_CONNECT,
        method: SimpleXHRMethod.POST,
        data: {
          email: trimmedEmail,
        },
        onSuccess: (response: any) => {
          const fee = response?.fee;
          dispatch(updateBitpayState({ email: trimmedEmail, fee }));
          onSuccess?.();
        },
        onError: (response: any) => {
          setErrorMessage(
            response?.errorDescription ||
              t('studio.earnings.generic.error.message')
          );
        },
      })
    );
  };

  return (
    <Modal
      className={s['wrapper']}
      contentClassName={s['content']}
      heading={t('studio.earnings.bitpay.withdraw.title')}
      onRequestClose={requestClose}
      showHeaderBorder
      showFooterBorder
      shouldCloseOnOverlayClick
      isOpen={isOpen}
      withScrollableContentWrapper
      footerRight={[
        <TextButton
          onClick={requestClose}
          size="large"
          variant="main"
          key="cancel"
        >
          {t('common.cancel')}
        </TextButton>,
        <BrandedButton
          onClick={handleContinue}
          size="large"
          variant="brand-green"
          key="continue"
          disabled={isLoading}
          loading={isLoading}
        >
          {t('common.continue')}
        </BrandedButton>,
      ]}
    >
      <WithdrawalErrorBlock message={errorMessage} />

      <div className={s['form-title']}>
        {t('studio.earnings.bitpay.withdraw.formTitle')}
      </div>

      <div className={s['subtitle']}>
        {t('studio.earnings.bitpay.withdraw.subtitle')}
      </div>

      <div className={s['email-section']}>
        <TextInput
          id="bitpay-email"
          label={t('studio.earnings.bitpay.withdraw.email.label')}
          value={email}
          onChange={handleEmailChange}
          placeholder="username@email.com"
          type="email"
          wrapperClassName={s['email-input']}
          validationResult={emailValidationError ? 'error' : undefined}
          details={emailValidationError || undefined}
        />
      </div>

      <div className={s['signup-link']}>
        {t('studio.earnings.bitpay.withdraw.signup.text')}{' '}
        <a
          href={BITPAY_SIGNUP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-link-level-2-primary"
        >
          {t('studio.earnings.bitpay.withdraw.signup.link')}
        </a>
      </div>
    </Modal>
  );
};

BitPayWithdrawSetupModal.displayName = 'BitPayWithdrawSetupModal';

export default BitPayWithdrawSetupModal;
